import React, { useReducer, useEffect } from 'react'
import * as R from 'ramda'
import _ from 'lodash'

import Title from 'shared/components/Title'
import useAxios from 'hooks/useAxios'
import { WebhookIntegration } from '.'
import { Loader } from 'shared/components/Loader'
import { reducer, initialState, reducerAction } from './reducer'
import { toast } from 'libs/toast'
import { IntegrationContainer } from 'components/IntegrationContainer'
import useUser from 'hooks/useUser'
import useSaveUserActivityLog, { INTEGRATION_ACTION_NAME } from 'hooks/useSaveUserActivityLog'

/**
 * @param {object} props
 * @param {'slack' | 'microsoft_teams'} props.type
 */
export default function WebhookIntegrationContainer({ type }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const title = _.startCase(_.toLower(type))
  const saveUserActivityLog = useSaveUserActivityLog()
  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)
  const logActionName =
    type === 'slack'
      ? INTEGRATION_ACTION_NAME.setup_slack_notification
      : INTEGRATION_ACTION_NAME.setup_microsoft_team

  const { data, fetch: fetchWebhookSetting } = useAxios({ url: `webhooks/settings/?type=${type}` })
  useEffect(() => {
    dispatch({
      type: reducerAction.SET_WEBHOOKSETTING_DATA,
      payload: { webhookSettings: R.propOr([], 'webhooks', data) },
    })
  }, [data])

  const { fetch: createWebhookSetting } = useAxios({
    url: 'webhooks/settings/',
    method: 'POST',
    skip: true,
  })
  const { fetch: deleteWebhookSetting } = useAxios({ skip: true, method: 'DELETE' })
  const { fetch: updateWebhookSetting } = useAxios({ skip: true, method: 'PATCH' })
  const { fetch: updateWebhookEvent } = useAxios({ skip: true, method: 'PATCH' })

  if (R.isNil(data)) {
    return (
      <React.Fragment>
        <IntegrationContainer>
          <div className="admin-heading">
            <h2>Send feedback to {title}</h2>
          </div>
          <Loader withContainer />
        </IntegrationContainer>
      </React.Fragment>
    )
  }

  function onCreateWebhookSetting(name, url) {
    dispatch({
      type: reducerAction.CREATE_WEBHOOKSETTING,
      payload: { saveButtonLoading: true, webhookSetting: [] },
    })
    createWebhookSetting({
      data: { type, name, url },
    })
      .then((response) => {
        toast.success(`${title} settings created!`)
        dispatch({
          type: reducerAction.CREATE_WEBHOOKSETTING,
          payload: {
            webhookSetting: response.data,
          },
        })
        saveUserActivityLog({
          action: logActionName,
          statusCode: response.status,
          requestBody: data,
          target: `com_${companyId}`,
        })
      })
      .catch((error) => {
        saveUserActivityLog({
          action: logActionName,
          statusCode: error.response.status,
          requestBody: data,
          errorResponseBody: error.response.data,
          target: `com_${companyId}`,
        })
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        dispatch({ type: reducerAction.TURN_OFF_SAVE_BUTTON_LOADING })
        throw error
      })
  }

  function onUpdateWebhookSetting(webhookSettingId, name, url) {
    updateWebhookSetting({
      url: `webhooks/settings/${webhookSettingId}/`,
      data: { type, name, url },
    })
      .then(() => {
        dispatch({
          type: 'update channel',
          payload: {
            webhookSettingId,
            name,
            url,
          },
        })

        toast.success(`${title} settings updated!`)
      })
      .catch((error) => {
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        throw error
      })
  }

  function onDeleteWebhookSetting(webhookSetting, selectedWebhookSettingIndex) {
    const { name, url, type, id } = webhookSetting
    deleteWebhookSetting({
      url: `webhooks/settings/${id}/`,
      data: { name, url, type },
    })
      .then(() => {
        dispatch({
          type: reducerAction.DELETE_WEBHOOKSETTING,
          payload: { selectedWebhookSettingIndex },
        })

        toast.success('Remove webhook setting successfully.')
      })
      .catch((error) => {
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        throw error
      })
  }

  /**
   * @param {import('components/IntegrationWebhookModal').WebhookSetting} webhookSetting
   * @param {'instant_notification' | 'summary_report'} eventType
   * @param {string} eventName
   * @param {boolean} value
   */
  function onUpdateWebhookEvent(webhookSetting, eventType, eventName, value, surveyToken) {
    dispatch({
      type: reducerAction.UPDATE_WEBHOOK_EVENT,
      payload: { webhookSetting, eventType, eventName, value, surveyToken },
    })
    updateWebhookEvent({
      url: `webhooks/settings/${webhookSetting.id}/event/`,
      data: {
        name: eventName,
        survey_url: `http://gateway:8000/api/survey/survey/${surveyToken}/`,
        enabled: value,
      },
    })
      .then(() => {
        fetchWebhookSetting({ url: `webhooks/settings/?type=${type}` })
        toast.success(`${title} settings updated!`)
      })
      .catch((error) => {
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        throw error
      })
  }

  function openAddChannelModal() {
    dispatch({ type: reducerAction.OPEN_ADD_WEBHOOKSETTING_MODAL })
  }

  function closeModal() {
    dispatch({ type: reducerAction.CLOSE_MODAL })
  }

  function handleSegmentSelect(index) {
    dispatch({
      type: reducerAction.OPEN_EDIT_WEBHOOKSETTING_MODAL,
      payload: { selectedWebhookSettingIndex: index, modalOpen: true },
    })
  }

  return (
    <React.Fragment>
      <IntegrationContainer>
        <Title title={`Send feedback to ${title}`} />
        <WebhookIntegration
          title={title}
          type={type}
          modalOpen={state.modalOpen}
          webhookSettings={state.webhookSettings}
          saveButtonLoading={state.saveButtonLoading}
          selectedWebhookSettingIndex={state.selectedWebhookSettingIndex}
          onCreateWebhookSetting={onCreateWebhookSetting}
          onUpdateWebhookSetting={onUpdateWebhookSetting}
          onDeleteWebhookSetting={onDeleteWebhookSetting}
          onUpdateWebhookEvent={onUpdateWebhookEvent}
          openAddChannelModal={openAddChannelModal}
          closeModal={closeModal}
          handleSegmentSelect={handleSegmentSelect}
        />
      </IntegrationContainer>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/WebhookIntegration/container.js